import { useCallback, useMemo } from 'react';
import ProjectRole from '@projectMembers/models/projectRole';
import { validateEmail as validateEmailFormat } from 'src/validators/emailValidator';
import Project from 'src/entities/project';
import useFetchWorkspaceMembersQuery from 'src/new_arch/modules/workspaces/queries/useFetchWorkspaceMembersQuery';

type UseEmailValidationParams = {
	project: Project;
	selectedRole: ProjectRole;
	emails: string[];
};

const useEmailValidation = ( { project, selectedRole, emails }: UseEmailValidationParams ) => {
	const { data: workspaceMembers } = useFetchWorkspaceMembersQuery( { workspaceID: project.workspaceID } );

	const workspaceMembersEmails = useMemo(
		() => workspaceMembers?.map( member => member.user.email ) ?? [],
		[ workspaceMembers ]
	);

	const validateEmail = useCallback( ( email: string ) => (
		validateEmailFormat( email ) && (
			!selectedRole.isManager || workspaceMembersEmails.includes( email )
		)
	), [ selectedRole, workspaceMembersEmails ] );

	const invalidManagerEmails = useMemo(
		() => emails.filter( email => !workspaceMembersEmails.includes( email ) ),
		[ emails, selectedRole, workspaceMembersEmails ]
	);

	const hasInvalidManagerEmails = selectedRole.isManager && invalidManagerEmails.length > 0;

	const allEmailsValid = useMemo(
		() => emails.length > 0 && emails.every( validateEmail ) && !hasInvalidManagerEmails,
		[ emails, validateEmail, hasInvalidManagerEmails ]
	);

	return {
		validateEmail,
		hasInvalidManagerEmails,
		allEmailsValid,
		invalidManagerEmails,
		workspaceMembersEmails
	};
};

export default useEmailValidation;
